import { useCallback, useRef } from 'react';
import { IconStatusCorrect, useToast } from '@adc/parallax-component-library';

import { useIDNCreateMutation } from '~/api/query/idnCreate';
import { useIDNUpdateMutation } from '~/api/query/idnUpdate';
import type { IDN } from '~/api/types';
import {
  IDNForm,
  type IDNFormRef,
  type IDNFormValues,
} from '~/components/forms/idn/IDNForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

type IDNFormModalProps = {
  testID: string;
  idn?: IDN;
  handleViewIDNProfile?: (idnId: string) => void;
};

export const IDNFormModal = ({
  testID,
  idn,
  handleViewIDNProfile,
}: IDNFormModalProps) => {
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const formRef = useRef<IDNFormRef>(null);
  const isEditing = Boolean(idn?.id);

  const { mutateAsync: createIDN } = useIDNCreateMutation();
  const { mutateAsync: updateIDN } = useIDNUpdateMutation();

  const onFormSubmit = useCallback(
    async (values: IDNFormValues) => {
      if (isEditing) {
        await updateIDN(values);

        closeModal('IDNForm');

        showToast({
          message: 'IDN profile updated',
          'aria-label': 'IDN profile updated',
          icon: <IconStatusCorrect accentColor="$support.positive.default" />,
        });
      } else {
        await createIDN(values);

        closeModal('IDNForm');

        openModal('IDNAdded', {
          idnId: values.id,
          idnName: values.name,
          testID: 'idn-added-modal',
          handleViewIDNProfile: () => {
            handleViewIDNProfile?.(values.id);
          },
        });
      }
    },
    [
      openModal,
      closeModal,
      isEditing,
      showToast,
      createIDN,
      updateIDN,
      handleViewIDNProfile,
    ],
  );

  return (
    <Modal
      testID={testID}
      title={isEditing ? `Edit IDN - ${idn?.name}` : 'Add IDN'}
      hasCloseButton
      size="m"
      primaryAction={{
        label: isEditing ? 'Save Changes' : 'Complete',
        onPress: () => {
          void formRef.current?.submitForm();
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('IDNForm');
        },
      }}
      onOpenChange={() => {
        closeModal('IDNForm');
      }}
    >
      <IDNForm
        ref={formRef}
        testID={buildTestId({
          testID,
          suffix: 'form',
        })}
        onSubmit={onFormSubmit}
        defaultValues={
          isEditing
            ? idn
              ? {
                  id: idn.id,
                  name: idn.name,
                  phone: idn.phone,
                  address: idn.address.mainLine,
                  addressLine2: idn.address.secondaryLine,
                  state: idn.address.state,
                  city: idn.address.city,
                  country: idn.address.country,
                  zipCode: idn.address.zipCode,
                }
              : undefined
            : undefined
        }
      />
    </Modal>
  );
};
